import { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import TooltipMui from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import './CreateCharts.scss';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';

export default function CreateCharts(props) {
  const className = 'issueDashboards__createCharts';

  const [monthsRange, setMonthsRange] = useState(6);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Normalize timestamps (assumed array of epoch numbers or ISO strings)
  const rawTimestamps = useMemo(() => {
    if (!Array.isArray(props.createTimeStampList)) return [];
    return props.createTimeStampList
      .map(t => {
        if (typeof t === 'number') return new Date(t);
        const d = new Date(t);
        return isNaN(d.valueOf()) ? null : d;
      })
      .filter(Boolean);
  }, [props.createTimeStampList]);

  // Build monthly buckets for last N months including current
  const data = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = monthsRange - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        key: `${d.getFullYear()}-${d.getMonth()}`,
        month: d.toLocaleString('en-US', { month: 'short' }) + ' ' + String(d.getFullYear()).slice(2),
        year: d.getFullYear(),
        m: d.getMonth(),
        created: 0
      });
    }
    if (rawTimestamps.length) {
      rawTimestamps.forEach(dt => {
        const k = `${dt.getFullYear()}-${dt.getMonth()}`;
        const bucket = buckets.find(b => b.key === k);
        if (bucket) bucket.created += 1;
      });
    }
    return buckets;
  }, [rawTimestamps, monthsRange]);

  const totalCreated = useMemo(
    () => data.reduce((a, b) => a + b.created, 0),
    [data]
  );
  const avgPerMonth = useMemo(
    () => (data.length ? (totalCreated / data.length).toFixed(1) : 0),
    [data, totalCreated]
  );

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 250); // small skeleton time
    return () => clearTimeout(t);
  }, [props.createTimeStampList, monthsRange]);

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    props.reloadData && props.reloadData();
    setTimeout(() => setRefreshing(false), 700);
  };

  const toggleRange = () => {
    setMonthsRange(r => (r === 6 ? 12 : 6));
  };

  const empty = !loading && data.every(d => d.created === 0);

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const val = payload[0].value;
    return (
      <div className={className + '__tooltip'}>
        <div className={className + '__tooltip__title'}>{label}</div>
        <div className={className + '__tooltip__row'}>
          <span>Tickets</span>
          <span className={className + '__tooltip__value'}>{val}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <div className={className + '__header'}>
        <div className={className + '__header__left'}>
          <div className={className + '__title'}>Tickets Created (Last {monthsRange} Months)</div>
        </div>
        <div className={className + '__header__actions'}>
            <div className={className + '__meta'}>
              <Chip
                size="medium"
                label={`Total: ${totalCreated}`}
                className={className + '__chip'}
              />
              <Chip
                size="medium"
                label={`Avg/mo: ${avgPerMonth}`}
                className={className + '__chip'}
              />
            </div>
            <TooltipMui title={`Switch to ${monthsRange === 6 ? '12' : '6'} months`}>
              <IconButton size="medium" onClick={toggleRange}>
                <ShowChartIcon fontSize="medium" />
              </IconButton>
            </TooltipMui>
            <TooltipMui title="Refresh">
              <IconButton
                size="medium"
                onClick={handleRefresh}
                className={refreshing ? 'rotating' : ''}
              >
                <RefreshIcon fontSize="medium" />
              </IconButton>
            </TooltipMui>
        </div>
      </div>

      {loading && (
        <div className={className + '__loader'}>
          <Skeleton variant="rectangular" height={260} style={{ borderRadius: 16 }} />
        </div>
      )}

      {!loading && empty && (
        <div className={className + '__empty'}>
          <div className={className + '__empty__text'}>No ticket creation activity in this period</div>
        </div>
      )}

      {!loading && !empty && (
        <div className={className + '__chart'}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="ticketsArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1976d2" stopOpacity={0.35} />
                  <stop offset="65%" stopColor="#1976d2" stopOpacity={0.08} />
                  <stop offset="100%" stopColor="#1976d2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-darkBorder)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: 'var(--color-primaryGrey)' }}
                stroke="var(--color-darkBorder)"
                height={32}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'var(--color-primaryGrey)' }}
                stroke="var(--color-darkBorder)"
              />
              <Tooltip content={customTooltip} />
              <Area
                type="monotone"
                dataKey="created"
                name="Created"
                stroke="#1976d2"
                strokeWidth={2}
                fill="url(#ticketsArea)"
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--color-darkBorder)' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
